import { useMutation, useQuery } from '@tanstack/react-query'
import { FirstLoginDestination, LoginMethod } from '@today/types'
import { InvitationFirstLoginStage } from '@today/ui-domain'
import { isAxiosError } from 'axios'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FORCED_MFA_ENROLMENT_PATH } from '@/auth/forced-mfa-enrolment'
import { stashInvitationToken } from '@/auth/invitation-token-stash'
import { stashPostLoginRedirect } from '@/auth/post-login-redirect'
import { authService } from '@/services/auth.service'
import { invitationService } from '@/services/invitation.service'

/** The API's machine-readable refusal code, if the error carries one. */
export const apiErrorCode = (error: unknown): string | null =>
	isAxiosError<{ error?: { code?: string } }>(error) ? (error.response?.data?.error?.code ?? null) : null

/**
 * Where a completed first login lands (REQ-003 / REQ-109 / SCN-005). A
 * required-but-unenrolled user is routed to forced TOTP enrolment, which takes
 * precedence over the normal onboarding/home destination.
 */
export const destinationPath = (slug: string, destination: FirstLoginDestination): string => {
	if (destination === FirstLoginDestination.ENROL_MFA) return FORCED_MFA_ENROLMENT_PATH
	return destination === FirstLoginDestination.ONBOARDING ? `/t/${slug}/onboarding` : '/dashboard'
}

const tokenFromHash = (hash: string): string | null => new URLSearchParams(hash.replace(/^#/, '')).get('token')

/**
 * The invitation page's logic (TO-309, design D10). Reads the link token from
 * the URL FRAGMENT once and strips it from the address bar, asks the server
 * which path the tenant uses, then either sets the chosen password (native)
 * and sends the user to sign in, or goes straight to the tenant's SAML or OIDC IdP
 * (federated), returning to `/t/:slug/invitation/complete`.
 */
export function useInvitationFirstLogin(slug: string) {
	const location = useLocation()
	const navigate = useNavigate()
	const [token] = useState(() => tokenFromHash(location.hash))

	useEffect(() => {
		if (location.hash) void navigate({ pathname: location.pathname, search: location.search }, { replace: true })
	}, [location.hash, location.pathname, location.search, navigate])

	const start = useQuery({
		queryKey: ['invitation-begin', slug, token],
		queryFn: () => invitationService.begin(slug, token as string),
		enabled: token !== null,
		retry: false,
		staleTime: Number.POSITIVE_INFINITY,
	})
	// SAML or OIDC (TO-378): any non-native method goes through the hosted UI.
	const federated = start.data !== undefined && start.data.method !== LoginMethod.NATIVE

	useEffect(() => {
		if (!federated || !start.data || !token) return
		stashInvitationToken(slug, token)
		stashPostLoginRedirect(`/t/${slug}/invitation/complete`)
		void authService.initiateLogin({ identityProvider: start.data.identityProvider })
	}, [federated, start.data, slug, token])

	const accept = useMutation({
		mutationFn: (newPassword: string) => invitationService.accept(slug, token as string, newPassword),
		onSuccess: ({ destination }) => {
			stashPostLoginRedirect(destinationPath(slug, destination))
			void authService.initiateLogin({ identityProvider: 'COGNITO' })
		},
	})
	const acceptCode = accept.isError ? apiErrorCode(accept.error) : null
	const passwordRejected = acceptCode === 'PASSWORD_POLICY_VIOLATION'

	return {
		stage: stageOf({
			token,
			start,
			federated,
			accepted: accept.isSuccess,
			acceptFailed: accept.isError && !passwordRejected,
		}),
		email: start.data?.email,
		errorCode: start.isError ? apiErrorCode(start.error) : acceptCode,
		passwordRejected,
		isSubmitting: accept.isPending,
		submitPassword: (password: string) => accept.mutate(password),
	}
}

function stageOf(s: {
	token: string | null
	start: { isLoading: boolean; isError: boolean }
	federated: boolean
	accepted: boolean
	acceptFailed: boolean
}): InvitationFirstLoginStage {
	if (!s.token || s.start.isError || s.acceptFailed) return InvitationFirstLoginStage.ERROR
	if (s.start.isLoading) return InvitationFirstLoginStage.LOADING
	if (s.federated || s.accepted) return InvitationFirstLoginStage.REDIRECTING
	return InvitationFirstLoginStage.SET_PASSWORD
}
